// Detalle y estadísticas de una campaña: métricas con %, contactos por métrica, enlaces
// clicados, motivos de fallo, vista del correo, listas y exportación CSV.
import type { JSX } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Ban, Copy, Download, ExternalLink, Pencil, RotateCw } from "lucide-react";
import { api } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { Badge, Button, Card, ErrorState, Loading } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { EmailFrame } from "./EmailFrame.tsx";
import { type Campaign, type CampaignStats, type CampaignStatus, fmtDate, pct, STATUS_LABEL } from "./types.ts";

interface Report { status: CampaignStatus; stats: CampaignStats; links: Array<{ id: string; url: string; clicks: number }>; errors: Array<{ error: string; n: number }> }
interface RecipientRow {
  id: string; email: string; contactId: string | null; name: string | null; status: string; error: string | null;
  sentAt: string | null; openedAt: string | null; openCount: number; clickedAt: string | null; clickCount: number; unsubscribedAt: string | null;
}
const METRIC_LABEL: Record<string, string> = {
  all: "Todos los destinatarios", sent: "Entregados", opened: "Abrieron", clicked: "Hicieron clic", failed: "Fallidos / rebotes",
  unsubscribed: "Bajas", pending: "Pendientes", skipped: "Omitidos",
};
const R_STATUS: Record<string, { label: string; color: string }> = {
  pending: { label: "Pendiente", color: "gray" }, sending: { label: "Enviando", color: "amber" }, sent: { label: "Entregado", color: "green" },
  failed: { label: "Fallido", color: "red" }, bounced: { label: "Rebote", color: "red" }, skipped: { label: "Omitido", color: "gray" },
};

export function CampaignDetail(): JSX.Element {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const isAdmin = Boolean(useMe().data?.admin);
  const [sp, setSp] = useSearchParams();
  const metric = sp.get("m") ?? "";
  const link = sp.get("link") ?? "";
  const page = Math.max(1, Number(sp.get("p")) || 1);

  const camp = useQuery({ queryKey: ["mkt-campaign", id], queryFn: () => api.get<Campaign>(`/mkt/campaigns/${id}`) });
  const sending = camp.data?.status === "sending";
  const report = useQuery({
    queryKey: ["mkt-campaign-report", id], queryFn: () => api.get<Report>(`/mkt/campaigns/${id}/report`),
    refetchInterval: (q) => (q.state.data?.status === "sending" ? 8000 : false),
  });
  const recips = useQuery({
    queryKey: ["mkt-campaign-recipients", id, metric, link, page],
    queryFn: () => api.get<{ items: RecipientRow[]; total: number; pageSize: number }>(`/mkt/campaigns/${id}/recipients?metric=${metric || "all"}${link ? `&link=${link}` : ""}&page=${page}`),
    enabled: Boolean(metric || link),
  });
  const refresh = (): void => { void qc.invalidateQueries({ queryKey: ["mkt-campaign", id] }); void qc.invalidateQueries({ queryKey: ["mkt-campaign-report", id] }); void qc.invalidateQueries({ queryKey: ["mkt-campaigns"] }); };
  const cancel = useMutation({
    mutationFn: () => api.post<{ status: string }>(`/mkt/campaigns/${id}/cancel`),
    onSuccess: (r) => { toast.success(r.status === "draft" ? "Programación cancelada: la campaña vuelve a borrador." : "Envío cancelado."); refresh(); },
    onError: (e) => toast.error(e.message),
  });
  const resend = useMutation({
    mutationFn: () => api.post<{ id: string; recipients: number }>(`/mkt/campaigns/${id}/resend`),
    onSuccess: (r) => { toast.success(`Borrador creado para ${r.recipients.toLocaleString("es-CO")} contactos que no abrieron.`); nav(`/marketing/campanas/${r.id}/editar`); },
    onError: (e) => toast.error(e.message),
  });
  const dup = useMutation({
    mutationFn: () => api.post<{ id: string }>(`/mkt/campaigns/${id}/duplicate`),
    onSuccess: (r) => { toast.success("Campaña duplicada."); nav(`/marketing/campanas/${r.id}/editar`); },
    onError: (e) => toast.error(e.message),
  });

  if (camp.isLoading) return <Loading />;
  if (camp.isError || !camp.data) return <ErrorState message={camp.error?.message ?? "No se pudo cargar la campaña."} onRetry={() => void camp.refetch()} />;
  const c = camp.data;
  const st: CampaignStats = report.data?.stats ?? c.stats ?? {};
  const status = report.data?.status ?? c.status;
  const recipients = st.recipients ?? st.sent ?? 0;
  const delivered = st.delivered ?? st.sent ?? 0;
  const failed = (st.failed ?? 0) + (st.bounces ?? 0);
  const imported = c.source !== "tiger";

  const cards: Array<{ key: string; label: string; n: number; base: number; tone?: string }> = [
    { key: "all", label: "Destinatarios", n: recipients, base: 0 },
    { key: "sent", label: "Entregados", n: delivered, base: recipients },
    { key: "opened", label: "Abiertos", n: st.uniqueOpens ?? 0, base: delivered },
    { key: "clicked", label: "Clics", n: st.uniqueClicks ?? 0, base: delivered },
    { key: "failed", label: "Rebotes / fallidos", n: failed, base: recipients, tone: failed ? "text-danger" : "" },
    { key: "unsubscribed", label: "Bajas", n: st.unsubscribes ?? 0, base: delivered },
  ];
  const setMetric = (m: string, l = ""): void => setSp(m || l ? { ...(m ? { m } : {}), ...(l ? { link: l } : {}) } : {});

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/marketing/campanas" className="btn-ghost btn-sm"><ArrowLeft size={14} className="mr-1 inline" />Campañas</Link>
        <div className="min-w-0">
          <h2 className="sec-title truncate">{c.name}</h2>
          <div className="text-xs text-ink-muted">{c.subject ?? "Sin asunto"} · {c.creator ?? "—"} · {status === "scheduled" ? `Programada para ${fmtDate(c.scheduledAt)}` : `Envío: ${fmtDate(c.sentAt)}`}</div>
        </div>
        <Badge color={STATUS_LABEL[status]?.color ?? "gray"}>{STATUS_LABEL[status]?.label ?? status}</Badge>
        {imported && <Badge color="purple">Importada de Clientify</Badge>}
        <div className="ml-auto flex flex-wrap gap-2">
          {isAdmin && c.status === "draft" && !imported && <Link className="btn-primary btn-sm" to={`/marketing/campanas/${id}/editar`}><Pencil size={14} className="mr-1 inline" />Editar</Link>}
          {isAdmin && (status === "sending" || status === "scheduled") && (
            <Button small variant="danger" disabled={cancel.isPending} onClick={() => { if (confirm(status === "sending" ? "¿Cancelar el envío? Los correos pendientes no se enviarán." : "¿Cancelar la programación? La campaña vuelve a borrador.")) cancel.mutate(); }}>
              <Ban size={14} className="mr-1 inline" />{status === "sending" ? "Cancelar envío" : "Cancelar programación"}
            </Button>
          )}
          {isAdmin && c.channel === "email" && status === "sent" && !imported && <Button small disabled={resend.isPending} onClick={() => resend.mutate()} title="Crea un borrador para quienes no abrieron. No envía nada todavía."><RotateCw size={14} className="mr-1 inline" />Reenviar a no abiertos</Button>}
          {isAdmin && c.channel === "email" && !imported && <Button small onClick={() => dup.mutate()} disabled={dup.isPending}><Copy size={14} className="mr-1 inline" />Duplicar</Button>}
          <a className="btn-secondary btn-sm" href={`/api/v1/mkt/campaigns/${id}/export.csv`} download><Download size={14} className="mr-1 inline" />Exportar CSV</a>
        </div>
      </div>

      {sending && (
        <Card className="p-4">
          <div className="flex justify-between text-sm mb-1.5"><span className="text-ink">Enviando…</span><span className="tnum text-ink-secondary">{((st.sent ?? 0) + (st.failed ?? 0) + (st.skipped ?? 0)).toLocaleString("es-CO")} / {recipients.toLocaleString("es-CO")}</span></div>
          <div className="h-2 rounded-full bg-surface-hover overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: pct((st.sent ?? 0) + (st.failed ?? 0) + (st.skipped ?? 0), recipients).replace(",", ".") }} /></div>
          <p className="text-xs text-ink-muted mt-1.5">El envío es escalonado para respetar los límites de Microsoft 365.</p>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {cards.map((k) => (
          <button key={k.key} type="button" disabled={imported} onClick={() => setMetric(metric === k.key ? "" : k.key)}
            className={`card p-4 text-left transition ${metric === k.key ? "ring-2 ring-primary" : imported ? "" : "hover:shadow-md"}`}>
            <div className="dt-label">{k.label}</div>
            <div className={`text-2xl font-semibold tnum mt-1 ${k.tone || "text-ink"}`}>{k.n.toLocaleString("es-CO")}</div>
            {k.base > 0 && <div className="text-xs text-ink-muted tnum">{pct(k.n, k.base)}</div>}
          </button>
        ))}
      </div>
      {(st.opens ?? 0) > 0 && <p className="text-xs text-ink-muted -mt-2">Aperturas totales: {(st.opens ?? 0).toLocaleString("es-CO")} · Clics totales: {(st.clicks ?? 0).toLocaleString("es-CO")}. Las aperturas pueden estar sobreestimadas por la precarga de imágenes de algunos clientes (p. ej. Apple Mail).</p>}

      {(metric || link) && (
        <Card className="p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b border-line">
            <div className="sec-title">{link ? `Clics en: ${report.data?.links.find((l) => l.id === link)?.url ?? "enlace"}` : METRIC_LABEL[metric] ?? "Destinatarios"} {recips.data && <span className="text-ink-muted font-normal text-sm">({recips.data.total.toLocaleString("es-CO")})</span>}</div>
            <button className="btn-ghost btn-sm" onClick={() => setMetric("")}>Cerrar</button>
          </div>
          {recips.isLoading ? <Loading /> : recips.isError ? <ErrorState message={recips.error.message} /> : !recips.data?.items.length ? <p className="p-6 text-sm text-ink-muted text-center">No hay contactos en esta métrica.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px]">
                <thead><tr><th className="th">Contacto</th><th className="th">Estado</th><th className="th">Enviado</th><th className="th">Abierto</th><th className="th">Clics</th><th className="th">Baja</th></tr></thead>
                <tbody>
                  {recips.data.items.map((r) => (
                    <tr key={r.id}>
                      <td className="td">
                        {r.contactId ? <Link className="link" to={`/contacts/${r.contactId}`}>{r.name ?? r.email}</Link> : <span className="text-ink">{r.name ?? r.email}</span>}
                        <div className="text-xs text-ink-muted">{r.email}</div>
                      </td>
                      <td className="td"><Badge color={R_STATUS[r.status]?.color ?? "gray"}>{R_STATUS[r.status]?.label ?? r.status}</Badge>{r.error && <div className="text-[11px] text-danger mt-0.5 max-w-xs">{r.error}</div>}</td>
                      <td className="td whitespace-nowrap">{fmtDate(r.sentAt)}</td>
                      <td className="td whitespace-nowrap">{r.openedAt ? <>{fmtDate(r.openedAt)} <span className="text-xs text-ink-muted">×{r.openCount}</span></> : "—"}</td>
                      <td className="td">{r.clickCount || "—"}</td>
                      <td className="td whitespace-nowrap">{fmtDate(r.unsubscribedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {recips.data.total > recips.data.pageSize && (
                <div className="flex items-center justify-end gap-2 px-4 py-2 border-t border-line text-xs text-ink-secondary">
                  <Button small disabled={page <= 1} onClick={() => setSp({ ...Object.fromEntries(sp), p: String(page - 1) })}>Anterior</Button>
                  <span>Página {page} de {Math.ceil(recips.data.total / recips.data.pageSize)}</span>
                  <Button small disabled={page * recips.data.pageSize >= recips.data.total} onClick={() => setSp({ ...Object.fromEntries(sp), p: String(page + 1) })}>Siguiente</Button>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px] items-start">
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-line sec-title">Vista del correo</div>
          <div className="bg-canvas p-3">
            {c.hasHtml ? <EmailFrame src={`/api/v1/mkt/campaigns/${id}/preview`} minHeight={640} autoHeight={false} /> : <p className="p-8 text-center text-sm text-ink-muted">{imported ? "Clientify no tiene contenido guardado para esta campaña." : "Sin contenido."}</p>}
          </div>
        </Card>
        <div className="space-y-4">
          <Card className="p-4">
            <div className="sec-title mb-2">Enlaces clicados</div>
            {!report.data?.links.length ? <p className="text-sm text-ink-muted">{imported ? "Sin detalle de enlaces." : "Sin enlaces rastreables."}</p> : (
              <ul className="divide-y divide-line">
                {report.data.links.map((l) => (
                  <li key={l.id} className="py-2 flex items-center gap-2 text-sm">
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="link truncate flex-1" title={l.url}>{l.url}</a>
                    <ExternalLink size={12} className="text-ink-muted shrink-0" />
                    <button type="button" className="tnum text-ink font-medium hover:underline shrink-0" title="Ver contactos" onClick={() => setMetric("", l.id)} disabled={!l.clicks}>{l.clicks.toLocaleString("es-CO")}</button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          {(report.data?.errors.length ?? 0) > 0 && (
            <Card className="p-4">
              <div className="sec-title mb-2">Motivos de fallo / rebote</div>
              <table className="w-full"><tbody>
                {report.data!.errors.map((e, i) => <tr key={i}><td className="py-1.5 text-sm text-ink-secondary">{e.error}</td><td className="py-1.5 text-sm text-right tnum text-ink">{e.n.toLocaleString("es-CO")}</td></tr>)}
              </tbody></table>
            </Card>
          )}
          <Card className="p-4 space-y-3 text-sm">
            <div>
              <div className="dt-label mb-1">Listas de destinatarios</div>
              {c.lists.length ? <div className="flex flex-wrap gap-1">{c.lists.map((l) => <Badge key={l.id} color="blue">{l.name}</Badge>)}</div> : <span className="text-ink-muted">—</span>}
            </div>
            <div>
              <div className="dt-label mb-1">Listas de exclusión</div>
              {c.excludeLists.length ? <div className="flex flex-wrap gap-1">{c.excludeLists.map((l) => <Badge key={l.id} color="red">{l.name}</Badge>)}</div> : <span className="text-ink-muted">—</span>}
            </div>
            <div className="grid grid-cols-[104px_minmax(0,1fr)] gap-x-2 gap-y-1.5 [&>span:nth-child(even)]:break-words">
              <span className="text-ink-muted">Remitente</span><span className="text-ink">{c.fromName ?? "—"}</span>
              <span className="text-ink-muted">Responder a</span><span className="text-ink">{c.replyTo ?? "—"}</span>
              <span className="text-ink-muted">Preencabezado</span><span className="text-ink">{c.preheader ?? "—"}</span>
              <span className="text-ink-muted">Carpeta</span><span className="text-ink">{c.folder ?? "—"}</span>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
